import React, { FC, useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";
import { List, Map } from "immutable";
import * as Sentry from "@sentry/browser";
import ExcelManager from "helpers/excel-manager";
import FileManager from "helpers/file-manager";
import { validateUPCData } from "helpers/validators/import-UPC-validator";
import {
    getDisplayNamesForIdentifierField,
    parseUPCData,
    getNewUpcsByProductId,
    mutateProductsWithNewUpc,
} from "helpers/utils/upc";
import { IUPCData, IUPCDataError, ProductList, UPCRuleType } from "helpers/validators/import-UPC-validator/types";
import { IStore } from "reducers";
import {
    getCollection,
    getCollectionItemDisplayProperty,
    getCollectionItems,
    ITEMS_OBJECT_ID_PATH,
} from "reducers/selectors/collection";
import { UPCUploaderContext } from "./upc-uploader-context";
import * as actions from "actions/collections";
import { IMutateCollectionItemPayload } from "actions/collections";
import { formatMessage } from "lib/globalize-util";
import { ImportUpcStatus } from "../../types";

interface IReduxProps {
    merchantIdentifierFields: string[];
    collectionSchemaKeys: List<Map<string, string>>;
    collectionItems: ProductList;
    hasUnfinishedMutations: boolean;
}

interface IReduxActions {
    onMutateCollectionItem: (params: IMutateCollectionItemPayload) => void;
}

type IProps = IReduxProps & IReduxActions;

const BaseUPCUploaderProvider: FC<IProps> = ({
    children,
    collectionItems,
    merchantIdentifierFields,
    collectionSchemaKeys,
    onMutateCollectionItem,
    hasUnfinishedMutations,
}) => {
    const [errorMessages, setErrorMessages] = useState<IUPCDataError>({});
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [wasProcessed, setWasProcessed] = useState<boolean>(false);
    const [collectionStartedToMutate, setCollectionStartedToMutate] = useState<boolean>(false);
    const [importUpcStatus, setImportUpcStatus] = useState<ImportUpcStatus>(ImportUpcStatus.NOT_STARTED);
    const [hasDataToChange, setHasDataToChange] = useState<boolean>(false);
    const [normalizeUPCData, setNormalizeUPCData] = useState<IUPCData[]>([]);
    const normalizedIdentifierFields = getDisplayNamesForIdentifierField(
        merchantIdentifierFields,
        collectionSchemaKeys
    );
    const [showTitleError, setShowTitleError] = useState(true);

    const handleErrorWhileProcessing = (errors: IUPCDataError) => {
        setErrorMessages(errors);
    };

    const resetStates = useCallback(() => {
        setShowTitleError(true);
        setWasProcessed(false);
        setHasDataToChange(false);
        handleErrorWhileProcessing(undefined);
    }, []);

    const handleSuccessImportUpcs = useCallback(() => {
        resetStates();
        setImportUpcStatus(ImportUpcStatus.FINISHED);
    }, [resetStates]);

    React.useEffect(() => {
        if (!collectionStartedToMutate && hasUnfinishedMutations) {
            setCollectionStartedToMutate(true);
            return;
        }

        if (collectionStartedToMutate && !hasUnfinishedMutations) {
            setCollectionStartedToMutate(false);
            handleSuccessImportUpcs();
        }
    }, [collectionStartedToMutate, handleSuccessImportUpcs, hasUnfinishedMutations]);

    const handleDropFile = useCallback(
        async (file: File, onSettled: (error: boolean) => void) => {
            setIsUploading(true);
            resetStates();

            try {
                const result = await FileManager.readAsArrayBuffer(file);
                const upcData = ExcelManager.read(result);
                const parsedData = parseUPCData(upcData, collectionSchemaKeys);
                const errors = validateUPCData({
                    data: parsedData,
                    products: collectionItems,
                    identifierFields: normalizedIdentifierFields,
                });
                const hasErrors = Object.keys(errors || {}).length > 0;

                onSettled(hasErrors);
                if (hasErrors) {
                    handleErrorWhileProcessing(errors);
                    return;
                }
                setNormalizeUPCData(parsedData);
                setHasDataToChange(true);
            } catch (error) {
                setShowTitleError(false);
                handleErrorWhileProcessing({
                    [UPCRuleType.GenericError]: formatMessage(
                        "Oops! Something went wrong. Please refresh and try again."
                    ),
                });
                Sentry.captureException(error);
                // eslint-disable-next-line no-console
                console.error(error);
            } finally {
                setIsUploading(false);
                setWasProcessed(true);
            }
        },

        [collectionItems, collectionSchemaKeys, normalizedIdentifierFields, resetStates]
    );

    const handleImport = useCallback(() => {
        const newUpcsByProductId = getNewUpcsByProductId(merchantIdentifierFields, normalizeUPCData, collectionItems);

        setImportUpcStatus(ImportUpcStatus.STARTED);
        mutateProductsWithNewUpc(newUpcsByProductId, collectionItems, onMutateCollectionItem);
    }, [collectionItems, merchantIdentifierFields, normalizeUPCData, onMutateCollectionItem]);

    const memoizedContextValue = useMemo(
        () => ({
            handleDropFile,
            handleImport,
            resetStates,
            errorMessages,
            isUploading,
            wasProcessed,
            hasDataToChange,
            showTitleError,
            importUpcStatus,
        }),
        [
            handleDropFile,
            handleImport,
            resetStates,
            errorMessages,
            isUploading,
            wasProcessed,
            hasDataToChange,
            showTitleError,
            importUpcStatus,
        ]
    );

    return <UPCUploaderContext.Provider value={memoizedContextValue}>{children}</UPCUploaderContext.Provider>;
};

const mapStateToProps = (state: IStore): IReduxProps => {
    const collectionId = state.collection?.collectionId;
    const collection = getCollection(state, collectionId);
    const collectionItems = getCollectionItems(state, collectionId);
    const itemsId = collection?.getIn(ITEMS_OBJECT_ID_PATH);
    const unfinishedMutationsSize = state?.mutate?.uncommittedMutations?.get(itemsId)?.size;
    const collectionSchemaKeys = getCollectionItemDisplayProperty(state, collectionId);

    return {
        merchantIdentifierFields: state.collection?.merchantIdentifierFields,
        collectionItems,
        collectionSchemaKeys,
        hasUnfinishedMutations: unfinishedMutationsSize > 0,
    };
};

const mapDispatchToProps = (dispatch, _) => {
    return {
        onMutateCollectionItem: (params: IMutateCollectionItemPayload) =>
            dispatch(actions.mutateCollectionItem(params)),
    };
};

export const UPCUploaderProvider = connect<IReduxProps, unknown, unknown>(
    mapStateToProps,
    mapDispatchToProps
)(BaseUPCUploaderProvider);
